import type { ControlBuild } from '../domain/nodeBuild';
import type { AutopilotState, Cluster, PanelRole, PanelUser, WarmupJob } from '../domain/panel';

export type PanelUserDto = PanelUser;
export type PanelRoleDto = PanelRole;

type Session = {
  username?: string;
  displayName?: string;
  role?: string;
  permissions?: string[];
  userId?: string;
};

type AutopilotResponse = { autopilot?: AutopilotState; pipeline?: AutopilotState };
type WarmupPoolResponse = { remaining?: number };

export type PanelApi = {
  auth: {
    me(): Promise<Session>;
    login(creds: { username: string; password: string }): Promise<Session>;
    logout(): Promise<unknown>;
  };
  users: {
    list(): Promise<{ users: PanelUser[] }>;
    create(body: unknown): Promise<{ user: PanelUser; password?: string }>;
    update(id: string, body: unknown): Promise<{ user: PanelUser; password?: string }>;
    remove(id: string): Promise<{ ok: boolean }>;
  };
  roles: {
    list(): Promise<{ roles: PanelRole[] }>;
    create(body: unknown): Promise<{ role: PanelRole }>;
    update(id: string, body: unknown): Promise<{ role: PanelRole }>;
    remove(id: string): Promise<{ ok: boolean }>;
  };
  tasks: {
    list(): Promise<unknown>;
    chart(): Promise<{ chart?: unknown }>;
    create(payload: unknown): Promise<{ task?: { id?: string } }>;
    update(id: string, payload: unknown): Promise<unknown>;
    remove(id: string): Promise<unknown>;
    setStatus(id: string, action: string): Promise<unknown>;
    bulk(ids: string[], action: string): Promise<unknown>;
  };
  scenarios: {
    list(): Promise<unknown>;
    create(input: unknown): Promise<unknown>;
    update(id: string, input: unknown): Promise<unknown>;
    remove(id: string): Promise<unknown>;
  };
  proxyPools: {
    list(): Promise<unknown>;
    upload(payload: unknown): Promise<unknown>;
    update(id: string, body: unknown): Promise<unknown>;
    remove(id: string): Promise<unknown>;
    clear(): Promise<unknown>;
  };
  proxies: {
    parse(content: string, protocol?: string): Promise<unknown>;
    list(): Promise<unknown>;
    remove(indexes: number[]): Promise<unknown>;
  };
  logs: {
    report(params: string): Promise<unknown>;
    query(params?: URLSearchParams | string): Promise<unknown>;
  };
  settings: {
    get(): Promise<unknown>;
    save(payload: unknown): Promise<unknown>;
    stopAll(): Promise<unknown>;
    resetStats(): Promise<{ reset?: number; failed?: string[] }>;
    telegramTest(): Promise<{ chatId?: string }>;
    password(body: { current: string; next: string }): Promise<{ ephemeral?: boolean }>;
  };
  clusters: {
    list(): Promise<{ clusters?: Cluster[]; control?: ControlBuild }>;
    create(body: unknown): Promise<{ deploying?: boolean; hasSshPassword?: boolean }>;
    update(id: string, body: unknown): Promise<unknown>;
    remove(id: string): Promise<unknown>;
    ping(id: string): Promise<unknown>;
    pingAll(): Promise<{ results?: Array<{ online?: boolean; deploying?: boolean }> }>;
    deploy(id: string): Promise<unknown>;
    profiles(id: string): Promise<unknown>;
    profileList(id: string): Promise<unknown>;
    profileLog(id: string, profile: string): Promise<unknown>;
    profilePersona(id: string, profile: string): Promise<unknown>;
    fpTest(id: string, run?: string): Promise<unknown>;
    fpTestStart(id: string, body: unknown): Promise<unknown>;
    fpShot(id: string, run: string, file: string): Promise<unknown>;
    profilesDelete(id: string, filter: unknown, dryRun: boolean): Promise<unknown>;
    profilesDeleteState(id: string): Promise<unknown>;
  };
  profileTemplate: {
    get(): Promise<unknown>;
    save(template: unknown): Promise<unknown>;
  };
  fpLibrary: {
    list(): Promise<unknown>;
    add(items: Array<{ name: string; data: string }>): Promise<unknown>;
    importPublic(): Promise<unknown>;
    remove(id: string): Promise<unknown>;
  };
  warmup: {
    jobs(): Promise<{ jobs?: WarmupJob[] }>;
    start(body: unknown, isControl: boolean): Promise<{ pooled?: number; remaining?: number; jobs?: WarmupJob[]; jobsEnqueued?: number }>;
    pool(): Promise<WarmupPoolResponse>;
    clearPool(): Promise<WarmupPoolResponse>;
  };
  autopilot: {
    get(): Promise<unknown>;
    save(body: unknown): Promise<AutopilotResponse>;
    start(body?: unknown): Promise<AutopilotResponse>;
    stop(): Promise<AutopilotResponse>;
  };
  ranks: {
    list(params?: { days?: number; taskId?: string; engine?: string }): Promise<import('../domain/ranks').RankMatrix>;
    probe(): Promise<{ ok?: boolean; probe?: import('../domain/ranks').RankProbeInfo }>;
  };
  clickTest: { run(payload: unknown): Promise<unknown> };
  clickCookies: {
    summary(): Promise<{ unused: number; used: number }>;
    upload(
      body: { name: string; content: string } | { items: Array<{ name: string; content: string }> },
    ): Promise<{ id?: string; cookies?: number; added?: number; skipped?: number; unused: number; used: number }>;
  };
  gallery: {
    list(): Promise<unknown>;
    upload(body: unknown): Promise<unknown>;
    remove(name: string): Promise<unknown>;
  };
};

export let Api: PanelApi;

export function configurePanelApi(adapter: PanelApi): void {
  Api = adapter;
}

let onUnauthorized: () => void = () => {};

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export function notifyUnauthorized(): void {
  onUnauthorized();
}
